import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import "./login.css";
import { useNavigate } from "react-router";
import {
  setAdminDashboardRole,
  getDefaultDashboardPath,
} from "../../../utils/adminAccess";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (token && token.trim() !== "") {
      navigate(getDefaultDashboardPath(), { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      return toast.error("Please fill out all fields.");
    }

    const adminData = {
      email,
      password,
    };

    try {
      setLoading(true);
      const response = await toast.promise(
        axios.post("/admin/login", adminData),
        {
          pending: "Logging in...",
          success: "Logged in successfully!",
        }
      );

      const token = response.data.token;
      const dashboardRole =
        response.data.dashboardRole ||
        response.data.admin?.dashboardRole ||
        "full";

      if (rememberMe) {
        localStorage.setItem("authToken", token);
      } else {
        sessionStorage.setItem("authToken", token);
      }
      setAdminDashboardRole(dashboardRole);

      navigate(getDefaultDashboardPath());
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <LoginInfo>
      <section className="login-page">
        <div className="container">
          <div className="login-form">
            <div className="form-header">
              <h1>Login in..!</h1>
              <p>Login to admin panel to control site.</p>
            </div>
            <form onSubmit={handleLogin}>
              <div className="form-inputs">
                <div className="form-group inputWithIcon">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    className="fa-envelope"
                  >
                    <path
                      d="M20.0456 4.08331H7.79557C5.21824 4.08331 3.12891 6.17265 3.12891 8.74998V19.25C3.12891 21.8273 5.21824 23.9166 7.79557 23.9166H20.0456C22.6229 23.9166 24.7122 21.8273 24.7122 19.25V8.74998C24.7122 6.17265 22.6229 4.08331 20.0456 4.08331Z"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.5"
                    />
                    <path
                      d="M3.18359 8.85498L11.5894 13.6733C12.2938 14.0821 13.0938 14.2973 13.9082 14.2973C14.7226 14.2973 15.5225 14.0821 16.2269 13.6733L24.6561 8.85498"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="form-group inputWithIcon">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="fa-envelope"
                  >
                    <path
                      d="M2 16C2 13.172 2 11.757 2.879 10.879C3.757 10 5.172 10 8 10H16C18.828 10 20.243 10 21.121 10.879C22 11.757 22 13.172 22 16C22 18.828 22 20.243 21.121 21.121C20.243 22 18.828 22 16 22H8C5.172 22 3.757 22 2.879 21.121C2 20.243 2 18.828 2 16Z"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.5"
                    />
                    <path
                      d="M6 10V8C6 6.4087 6.63214 4.88258 7.75736 3.75736C8.88258 2.63214 10.4087 2 12 2C13.5913 2 15.1174 2.63214 16.2426 3.75736C17.3679 4.88258 18 6.4087 18 8V10"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M8 16H8.009M11.991 16H12M15.991 16H16"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <i
                    className={`fa-regular ${
                      passwordVisible ? "fa-eye" : "fa-eye-slash"
                    }`}
                    onClick={togglePasswordVisibility}
                    style={{ cursor: "pointer" }}
                  ></i>
                </div>
              </div>

              <div className="login-list">
                <div className="login-remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <label htmlFor="rememberMe">Remember Me</label>
                </div>

                <a href="/dashboard/login/forget-password">Forget Password?</a>
              </div>
              <button type="submit" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
          <div className="login-background">
            <img
              src={
                process.env.PUBLIC_URL +
                "/photos/257cdf6122cadf5b56b96eddf37c41ea.webp"
              }
              alt="login-background"
            />
          </div>
        </div>
      </section>
    </LoginInfo>
  );
}

export function LoginInfo({ children }) {
  return children;
}

export default Login;
